import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { format } from "date-fns";
import { Clock, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { createTransaction, updateTransaction, matchService, searchCustomers } from "@/lib/api";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Unified Add Booking dialog (modeled on the legacy Daily Booking form).
// Branch + date are fixed by the Work Sheet context.
export default function AddBookingModal({ open, onOpenChange, branchId, date, staff = [], services = [], onCreated, prefill, editTransaction }) {
  const [form, setForm] = useState({
    client_name: "",
    staff_id: "",
    service_id: "",
    booking_status: "Walk-in",
    payment_method: "Cash",
    time_start: format(new Date(), "HH:mm"),
    price: "",
  });
  const [serviceName, setServiceName] = useState("");
  const [duration, setDuration] = useState("");
  const [selectedService, setSelectedService] = useState(null);
  const [exactPriceMatch, setExactPriceMatch] = useState(true);
  const [cashAmount, setCashAmount] = useState("");
  const [customerId, setCustomerId] = useState(null);
  const [clientPhone, setClientPhone] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [isDual, setIsDual] = useState(false);
  const [secondStaffId, setSecondStaffId] = useState("");

  const isSplit = form.payment_method === "Split";
  const totalPrice = parseFloat(form.price) || 0;
  const eftposAmount = isSplit ? round2(Math.max(0, totalPrice - (parseFloat(cashAmount) || 0))) : 0;

  // Clean service dropdown: only the raw service name (no durations).
  const serviceNames = useMemo(
    () => [...new Set(services.map((s) => s.name))].sort((a, b) => a.localeCompare(b)),
    [services]
  );

  // Duration options are derived from the catalog for the chosen service name.
  // Always include the currently-selected duration so edits pre-fill reliably.
  const durationOptions = useMemo(() => {
    if (!serviceName) return [];
    const set = new Set(services.filter((s) => s.name === serviceName).map((s) => s.duration));
    if (duration) set.add(parseInt(duration, 10));
    return [...set].sort((a, b) => a - b);
  }, [services, serviceName, duration]);

  // Reset when opened
  const resetForm = useCallback(() => {
    if (editTransaction) {
      const et = editTransaction;
      const dual = (et.therapist_ids || []).length > 1;
      setForm({
        client_name: et.client_name || "",
        staff_id: et.staff_id || "",
        service_id: et.service_id || "",
        booking_status: et.booking_status || "Walk-in",
        payment_method: et.payment_method || "Cash",
        time_start: et.time_start || format(new Date(), "HH:mm"),
        price: et.price != null ? String(et.price) : "",
      });
      setServiceName(et.service_name || "");
      setDuration(et.duration != null ? String(et.duration) : "");
      setSelectedService(null);
      setCashAmount(et.cash_amount != null ? String(et.cash_amount) : "");
      setExactPriceMatch(true);
      setCustomerId(et.customer_id || null);
      setClientPhone("");
      setSuggestions([]);
      setShowSuggest(false);
      setIsDual(dual);
      setSecondStaffId(dual ? et.therapist_ids[1] : "");
      return;
    }
    setForm({
      client_name: "",
      staff_id: prefill?.staff_id || "",
      service_id: "",
      booking_status: "Walk-in",
      payment_method: "Cash",
      time_start: prefill?.time_start || format(new Date(), "HH:mm"),
      price: "",
    });
    setServiceName("");
    setDuration("");
    setSelectedService(null);
    setCashAmount("");
    setExactPriceMatch(true);
    setCustomerId(null);
    setClientPhone("");
    setSuggestions([]);
    setShowSuggest(false);
    setIsDual(false);
    setSecondStaffId("");
  }, [prefill, editTransaction]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    Promise.resolve().then(() => { if (active) resetForm(); });
    return () => { active = false; };
  }, [open, resetForm]);

  // CRM autocomplete: search existing customers as the admin types.
  useEffect(() => {
    if (!open) return undefined;
    const q = (form.client_name || "").trim();
    if (q.length < 1) { setSuggestions([]); return undefined; }
    const t = setTimeout(async () => {
      try { setSuggestions(await searchCustomers(q)); } catch { setSuggestions([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [form.client_name, open]);

  // Resolve price/service when name + duration + payment chosen
  const resolveService = useCallback(async (name, dur, payment) => {
    if (!name || !dur || !payment) return;
    const lookup = payment === "Split" ? "Eftpos" : payment;
    try {
      const svc = await matchService(name, parseInt(dur, 10), lookup);
      setSelectedService(svc);
      setExactPriceMatch(svc.matched_payment_method !== false);
      setForm((prev) => ({ ...prev, service_id: svc.id, price: String(svc.base_price) }));
    } catch {
      setSelectedService(null);
      setForm((prev) => ({ ...prev, service_id: "", price: "" }));
    }
  }, []);

  useEffect(() => {
    let active = true;
    Promise.resolve().then(() => { if (active) resolveService(serviceName, duration, form.payment_method); });
    return () => { active = false; };
  }, [serviceName, duration, form.payment_method, resolveService]);

  const onServiceChange = (name) => {
    setServiceName(name);
    // Auto-pick duration if there's only one option for this service.
    const durs = [...new Set(services.filter((s) => s.name === name).map((s) => s.duration))];
    setDuration(durs.length === 1 ? String(durs[0]) : "");
    setSelectedService(null);
  };

  const endTime = () => {
    if (!form.time_start || !selectedService) return "--:--";
    const [h, m] = form.time_start.split(":").map(Number);
    const total = h * 60 + m + selectedService.duration;
    return `${String(Math.floor(total / 60) % 24).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!branchId || !form.client_name || !form.staff_id || !form.service_id) {
      toast.error("Please fill in Client, Therapist, Service and Duration");
      return;
    }
    if (isDual && (!secondStaffId || secondStaffId === form.staff_id)) {
      toast.error("Select a different second therapist for the 4-Hand session");
      return;
    }
    const total = form.price ? parseFloat(form.price) : null;
    let cash_amount = null;
    let eftpos_amount = null;
    if (isSplit) {
      cash_amount = parseFloat(cashAmount) || 0;
      if (cash_amount > (total || 0)) {
        toast.error("Cash amount cannot exceed the total price");
        return;
      }
      eftpos_amount = round2(Math.max(0, (total || 0) - cash_amount));
    }
    if (submittingRef.current) return; // synchronous re-entry lock (rapid double-click)
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const payload = {
        client_name: form.client_name,
        customer_id: customerId || undefined,
        client_phone: clientPhone || undefined,
        staff_id: form.staff_id,
        staff_ids: isDual ? [form.staff_id, secondStaffId].filter(Boolean) : (editTransaction ? [form.staff_id] : undefined),
        service_id: form.service_id,
        booking_status: form.booking_status,
        payment_method: form.payment_method,
        time_start: form.time_start,
        price: total,
        cash_amount,
        eftpos_amount,
      };
      if (editTransaction) {
        await updateTransaction(editTransaction.id, payload);
        toast.success("Session updated");
      } else {
        await createTransaction({
          date: format(date, "yyyy-MM-dd"),
          branch_id: branchId,
          ...payload,
        });
        toast.success("Booking added");
      }
      onCreated?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save booking");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-booking-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-forest">{editTransaction ? "Edit Session" : "New Booking"}</DialogTitle>
          <DialogDescription>
            {editTransaction ? "Adjust this session's details" : `Log a session for ${format(date, "EEEE, MMM d")}`}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2 relative">
              <Label className="text-sm text-forest">Client Name *</Label>
              <Input
                value={form.client_name}
                onChange={(e) => { setForm((p) => ({ ...p, client_name: e.target.value })); setCustomerId(null); setShowSuggest(true); }}
                onFocus={() => setShowSuggest(true)}
                onBlur={() => setTimeout(() => setShowSuggest(false), 150)}
                placeholder="Search or add new client"
                autoComplete="off"
                data-testid="booking-client-input"
              />
              {showSuggest && Array.isArray(suggestions) && suggestions.length > 0 && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-border rounded-lg shadow-lg max-h-44 overflow-y-auto" data-testid="customer-suggestions">
                  {suggestions.map((c) => (
                    <button
                      type="button"
                      key={c.id}
                      className="w-full text-left px-3 py-2 hover:bg-sand text-sm flex items-center justify-between gap-2"
                      onMouseDown={(e) => { e.preventDefault(); setForm((p) => ({ ...p, client_name: c.name })); setCustomerId(c.id); setClientPhone(c?.phone ?? ""); setShowSuggest(false); }}
                      data-testid={`customer-option-${c.id}`}
                    >
                      <span className="text-forest truncate">{c.name}{c.phone && <span className="text-sage"> ({c.phone})</span>}</span>
                      <span className="text-[10px] text-sage shrink-0">{c.visits} visit{c.visits === 1 ? "" : "s"}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-forest">Phone {customerId ? "" : "(new client)"}</Label>
              <Input
                value={clientPhone}
                onChange={(e) => setClientPhone(e.target.value)}
                placeholder="Optional"
                data-testid="booking-client-phone"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm text-forest">Therapist *</Label>
              <Select value={form.staff_id || undefined} onValueChange={(v) => setForm((p) => ({ ...p, staff_id: v }))}>
                <SelectTrigger data-testid="booking-staff-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-forest">Service *</Label>
              <Select value={serviceName || undefined} onValueChange={onServiceChange}>
                <SelectTrigger data-testid="booking-service-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {serviceNames.map((n) => (
                    <SelectItem key={n} value={n}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm text-forest">Duration *</Label>
              <Select key={`dur-${serviceName}-${duration}`} value={duration || undefined} onValueChange={setDuration} disabled={!serviceName}>
                <SelectTrigger data-testid="booking-duration-select"><SelectValue placeholder={serviceName ? "Select duration" : "Pick service first"} /></SelectTrigger>
                <SelectContent>
                  {durationOptions.map((d) => (
                    <SelectItem key={d} value={String(d)}>{d} min</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-forest">Booking Status</Label>
              <Select value={form.booking_status} onValueChange={(v) => setForm((p) => ({ ...p, booking_status: v }))}>
                <SelectTrigger data-testid="booking-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Walk-in">Walk-in</SelectItem>
                  <SelectItem value="Book">Booked</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* 4-Hand dual therapist */}
          <div className="space-y-3 rounded-lg border border-border p-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={isDual} onCheckedChange={(v) => { setIsDual(!!v); if (!v) setSecondStaffId(""); }} data-testid="booking-dual-toggle" />
              <span className="text-sm font-medium text-forest">4-Hand Massage (two therapists, 50/50 wage split)</span>
            </label>
            {isDual && (
              <div className="space-y-2">
                <Label className="text-sm text-forest">Second Therapist *</Label>
                <Select value={secondStaffId || undefined} onValueChange={(v) => setSecondStaffId(v ?? "")}>
                  <SelectTrigger data-testid="booking-second-staff-select"><SelectValue placeholder="Select second therapist" /></SelectTrigger>
                  <SelectContent>
                    {(staff ?? []).filter((s) => s?.id && s.id !== form?.staff_id).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm text-forest">Payment Method</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm((p) => ({ ...p, payment_method: v }))}>
                <SelectTrigger data-testid="booking-payment-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Eftpos">Eftpos</SelectItem>
                  <SelectItem value="Split">Split (Cash + Eftpos)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-forest">Start Time</Label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-sage" />
                <Input
                  type="time"
                  value={form.time_start}
                  onChange={(e) => setForm((p) => ({ ...p, time_start: e.target.value }))}
                  className="pl-10"
                  data-testid="booking-time-input"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm text-forest">Price ($)</Label>
            <Input
              type="number"
              step="0.01"
              value={form.price}
              onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))}
              placeholder="0.00"
              data-testid="booking-price-input"
            />
          </div>

          {selectedService && (
            <div className="flex items-center gap-3 text-xs text-sage bg-sand rounded-lg p-2">
              <span>End: <b className="text-forest">{endTime()}</b></span>
              <span>Duration: <b className="text-forest">{selectedService.duration}m</b></span>
              {!exactPriceMatch && <Badge className="bg-ochre/20 text-ochre">closest price</Badge>}
            </div>
          )}

          {isSplit && (
            <div className="grid grid-cols-2 gap-4 bg-sand rounded-lg p-3" data-testid="booking-split-section">
              <div className="space-y-2">
                <Label className="text-sm text-forest">Cash ($)</Label>
                <Input type="number" step="0.01" min="0" value={cashAmount}
                  onChange={(e) => setCashAmount(e.target.value)} placeholder="0.00" data-testid="booking-split-cash" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-forest">Eftpos ($) — auto</Label>
                <Input type="number" value={eftposAmount.toFixed(2)} readOnly className="bg-white cursor-not-allowed" data-testid="booking-split-eftpos" />
              </div>
            </div>
          )}

          <Button type="submit" className="w-full bg-forest hover:bg-forest-light text-white" disabled={submitting} data-testid="booking-submit-btn">
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
            {editTransaction ? "Save Changes" : "Create Booking"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
